import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { QrCode, Send } from 'lucide-react';
import { Botao } from '../../componentes/base';
import { Carregando, Erro } from '../../componentes/estados';
import { anunciarOcultos } from '../../componentes/sensivel';
import { Modal } from '../../componentes/sobreposicao';
import { TextoCortado } from '../../componentes/texto-cortado';
import { useToast } from '../../componentes/toast';
import { get, mensagemDeErro, post, textoDeErroGravado } from '../../nucleo/api';
import { data, dinheiro, email, nomePessoa, telefoneFormatado } from '../../nucleo/formato';
import { useTextoDoProvedor } from './provedor-texto';
import { invalidarContas, type DocumentoDetalhe, type EnvioManual, type PreviaEnvioCobranca } from './tipos';

type Escolha = 'email' | 'whatsapp' | 'ambos';
const CANAIS_DA_ESCOLHA: Record<Escolha, ('email' | 'whatsapp')[]> = { email: ['email'], whatsapp: ['whatsapp'], ambos: ['email', 'whatsapp'] };

/**
 * Fase 2 (W29): "Enviar cobrança agora" na ficha da fatura a receber em aberto. A confirmação mostra para onde vai (e-mail e,
 * com o WhatsApp conectado, o celular), deixa escolher o canal e avisa a cobrança do Asaas: gerando (espere) ou ausente com
 * o conector do provedor da regra conectado (ofereça gerar antes, para a mensagem levar o link ou o Pix; r51). O servidor monta a mesma mensagem da régua
 * (`POST /contas/documentos/:id/enviar-cobranca`, permissão `contas.cobrar`) e recusa o reenvio em menos de 1 minuto.
 */
export function EnviarCobranca({ doc, aberto, aoFechar, aoGerarAntes }: { doc: DocumentoDetalhe | null; aberto: boolean; aoFechar: () => void; aoGerarAntes?: () => void }) {
  if (!doc || !aberto) return null;
  return <Corpo doc={doc} aoFechar={aoFechar} aoGerarAntes={aoGerarAntes} />;
}

function Corpo({ doc, aoFechar, aoGerarAntes }: { doc: DocumentoDetalhe; aoFechar: () => void; aoGerarAntes?: () => void }) {
  const { t } = useTranslation('contas');
  const { t: tn } = useTranslation();
  const tp = useTextoDoProvedor();
  const toast = useToast();
  const qc = useQueryClient();
  const [escolha, setEscolha] = useState<Escolha | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const previa = useQuery({
    queryKey: ['/contas/documentos', 'enviar-cobranca', doc.id],
    queryFn: () => get<PreviaEnvioCobranca>(`/contas/documentos/${doc.id}/enviar-cobranca`),
    // Enquanto o Asaas gera a cobrança, a prévia se atualiza sozinha (o envio sai já com o link).
    refetchInterval: (q) => (q.state.data?.cobranca_estado === 'gerando' ? 3000 : false),
  });
  const p = previa.data;
  const temEmail = !!p?.email;
  const temWhats = !!p?.whatsapp_disponivel && !!p.telefone;
  const disponivel = (e: Escolha) => CANAIS_DA_ESCOLHA[e].every((c) => (c === 'email' ? temEmail : temWhats));
  // Padrão: os dois quando dá, senão o que houver.
  const padrao: Escolha | null = disponivel('ambos') ? 'ambos' : temEmail ? 'email' : temWhats ? 'whatsapp' : null;
  const atual = escolha && disponivel(escolha) ? escolha : padrao;
  const canais = atual ? CANAIS_DA_ESCOLHA[atual] : [];
  // Espera do reenvio contada na tela a partir da resposta do servidor; no fim, a prévia é relida.
  const [agora, setAgora] = useState(() => Date.now());
  const esperaServidor = p ? Math.max(0, ...canais.map((c) => p.espera_s[c] ?? 0)) : 0;
  const espera = esperaServidor > 0 ? Math.max(0, Math.ceil(esperaServidor - Math.max(0, agora - previa.dataUpdatedAt) / 1000)) : 0;
  useEffect(() => {
    if (esperaServidor <= 0) return;
    const id = setInterval(() => setAgora(Date.now()), 1000);
    return () => clearInterval(id);
  }, [esperaServidor, previa.dataUpdatedAt]);
  const gerando = p?.cobranca_estado === 'gerando';
  // Review r51 (SPEC-M1, ROB-M1): "tem cobrança" é a cobrança viva de qualquer provedor (o Pix do Sicredi não tem link), não
  // só o link; com ela, a prévia diz com o que a mensagem vai e não oferece "Gerar cobrança antes".
  // Review r52 (SPEC-B1, VIS-B2): `com_link` agora conta também o link do Portal; "tem cobrança" é o meio de pagar.
  const temCobranca = !!p && ((p.com_meio_de_pagar ?? p.com_link) || (p.cobranca_estado === 'gerada' && !p.pix_vencido));
  const semCobranca = !!p && !temCobranca && p.cobranca_estado !== 'gerando' && !p.pix_vencido;
  // Review r51 (VIS-B2, SPEC-B2): o atalho segue o conector do provedor da regra, não o Asaas; regra "nenhum" não oferece.
  const regraNenhum = p?.provedor_regra === 'nenhum';
  // rl0411-3 (VIS-B1): fatura fora de reais não ganha cobrança; cai no texto neutro, sem pedir para gerar antes.
  const podeGerarAntes = !!p && !regraNenhum && doc?.moeda === 'BRL' && (p.regra_conectada ?? p.asaas_conectado);

  const enviar = useMutation({
    mutationFn: () => post<{ envios: EnvioManual[]; com_link: boolean }>(`/contas/documentos/${doc.id}/enviar-cobranca`, { canais }),
    onSuccess: async (r) => {
      await invalidarContas(qc);
      const falhas = r.envios.filter((x) => x.resultado === 'falhou');
      const certos = r.envios.filter((x) => x.resultado === 'enviado');
      if (!falhas.length) toast.sucesso(t('w29.enviada'));
      else if (certos.length) toast.erro(t('w29.enviadaParcial', { ok: t(`canal.${certos[0]!.canal}`), falhou: t(`canal.${falhas[0]!.canal}`), erro: textoDeErroGravado(falhas[0]!.erro) }));
      else toast.erro(t('w29.falhou', { erro: textoDeErroGravado(falhas[0]!.erro) }));
      aoFechar();
    },
    onError: (e) => {
      setErro(mensagemDeErro(e));
      void previa.refetch();
    },
  });

  return (
    <Modal
      aberto
      aoFechar={aoFechar}
      titulo={t('w29.titulo', { numero: doc.numero })}
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.voltar')}
          </Botao>
          <Botao variante="primario" icone={<Send className="size-4" />} disabled={!p || !canais.length || gerando || espera > 0} carregando={enviar.isPending} onClick={() => (setErro(null), enviar.mutate())}>
            {t('w29.confirmar')}
          </Botao>
        </>
      }
    >
      {previa.isPending && <Carregando />}
      {previa.isError && <Erro erro={previa.error} aoTentar={() => previa.refetch()} />}
      {p && (
        <div className="flex flex-col gap-3 text-sm" data-enviar-cobranca>
          <p className="text-tinta-2">{anunciarOcultos(t('w29.mensagem', { nome: nomePessoa(doc.parceiro_nome), valor: dinheiro(doc.saldo_centavos, doc.moeda), vencimento: data(doc.vencimento) }))}</p>
          <div role="radiogroup" aria-label={t('w29.canalRotulo')} className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            {(['email', 'whatsapp', 'ambos'] as const).map((e) => (
              <button
                key={e}
                type="button"
                role="radio"
                aria-checked={atual === e}
                disabled={!disponivel(e)}
                onClick={() => setEscolha(e)}
                className={`h-10 flex-1 rounded-lg border-2 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50 ${atual === e ? 'border-acento-forte bg-acento-suave text-tinta-1' : 'border-tinta-3 text-tinta-2 hover:bg-sup-2'}`}
              >
                {t(`w29.canais.${e}`)}
              </button>
            ))}
          </div>
          <dl className="divide-y divide-borda rounded-lg border border-borda px-3">
            <div className="flex min-w-0 justify-between gap-4 py-1.5">
              <dt className="shrink-0 text-tinta-3">{t('canal.email')}</dt>
              <TextoCortado como="dd" className={`min-w-0 text-right ${p.email ? '' : 'text-tinta-3'}`} data-destino-email>
                {p.email ? email(p.email) : t(p.cliente_oculto ? 'w29.contatoOculto' : 'w29.semEmail')}
              </TextoCortado>
            </div>
            <div className="flex min-w-0 justify-between gap-4 py-1.5">
              <dt className="shrink-0 text-tinta-3">{t('canal.whatsapp')}</dt>
              <TextoCortado como="dd" className={`min-w-0 text-right ${temWhats ? '' : 'text-tinta-3'}`} data-destino-whatsapp>
                {!p.whatsapp_disponivel ? t('w29.whatsappDesligado') : p.telefone ? telefoneFormatado(p.telefone) : t(p.cliente_oculto ? 'w29.contatoOculto' : 'w29.semTelefone')}
              </TextoCortado>
            </div>
          </dl>
          {!p.whatsapp_disponivel && (
            <p className="text-xs text-tinta-3" data-motivo-whatsapp>
              {t(p.whatsapp_indisponivel_motivo === 'modulo' ? 'w29.whatsappModulo' : 'w29.whatsappConector')}
            </p>
          )}
          {gerando && (
            <p role="status" className="rounded-lg border border-borda px-3 py-2 text-tinta-2">
              {tp('w29.gerando', p.cobranca_provedor ?? 'asaas')}
            </p>
          )}
          {p.pix_vencido && (
            <p role="status" className="rounded-lg border border-borda px-3 py-2 text-tinta-2" data-pix-vencido>
              {t('w29.pixVencido')}
              {p.troca_erro ? ` ${t('w29.pixVencidoMotivo', { motivo: textoDeErroGravado(p.troca_erro) })}` : ''}
            </p>
          )}
          {temCobranca && (
            <p className="text-xs text-tinta-3" data-com-cobranca>
              {tp('w29.comLink', p.cobranca_provedor ?? 'asaas')}
            </p>
          )}
          {semCobranca && podeGerarAntes && (
            <div role="status" className="flex flex-col items-start gap-2 rounded-lg border border-laranja px-3 py-2" data-sem-cobranca>
              <p className="text-tinta-2">{tp('w29.semLinkAsaas', p.provedor_regra ?? null)}</p>
              {aoGerarAntes && (
                <Botao tamanho="sm" icone={<QrCode className="size-4" />} onClick={aoGerarAntes}>
                  {t('w29.gerarAntes')}
                </Botao>
              )}
            </div>
          )}
          {semCobranca && !podeGerarAntes && <p className="text-xs text-tinta-3">{t(regraNenhum ? 'w29.semLinkRegraNenhum' : 'w29.semLink')}</p>}
          {/* Review r52 (SPEC-B1, VIS-B2): sem meio de pagar, a mensagem ainda leva o link da fatura no Portal (só para ver). */}
          {semCobranca && p.com_portal && (
            <p className="text-xs text-tinta-3" data-com-portal>
              {t('w29.comPortalSemPagar')}
            </p>
          )}
          {espera > 0 && (
            <p role="status" className="text-xs text-laranja" data-espera>
              {t('w29.aguarde', { segundos: espera })}
            </p>
          )}
          {/* Review r19 spec (O2): cliente de outra empresa (W20): o contato existe, a pessoa é que não o vê. */}
          {!canais.length && (
            <p className="text-xs text-laranja" data-sem-destino>
              {t(p.cliente_oculto ? 'w29.clienteOculto' : 'w29.semDestino')}
            </p>
          )}
          {erro && (
            <p role="alert" className="rounded-lg border border-perigo px-3 py-2 text-perigo">
              {erro}
            </p>
          )}
        </div>
      )}
    </Modal>
  );
}
